import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type ChartsImperativeRef } from "@features/sales-v3/charts/charts";
import { ShareButton } from "@features/shared";
import { AttributionWindowSelect } from "@pages/sales/report-body-control/attribution-window-select";
import { NbAccountingModeSelect } from "@pages/sales/report-body-control/nb-accounting-mode-select";
import { AttributionModelSelect } from "@shared/selects";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { useSalesPageV3NextContext } from "../sales-v3-next-provider";
import { SaveSavedViewsMenu } from "../save-saved-views-menu";
import { SavedViewsMenu } from "../saved-views-menu";

import { ExportMenu } from "./export-menu";

export interface SavedViewBarProps {
  onShowConfigurationDetailsModal(): void;
}

export const SavedViewBar = ({
  onShowConfigurationDetailsModal,
}: SavedViewBarProps) => {
  const { isDemoMode } = usePrefs();
  const prettyLogEventPage = usePrettyLogEventPage();
  //   const { user } = useUser();
  //   const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  // const { hasFeatureFlag } = useFeatureFlags();
  //   const { openModal: openCustomMetricsModal } = useCustomMetricsContext();
  //   const [isCreateBreakdownModalOpen, setIsCreateBreakdownModalOpen] =
  //     useState(false);
  // const [doNotShowCharts, setDoNotShowCharts] = useDoNotShowCharts();
  // const showCharts = useMemo(() => {
  //   return !doNotShowCharts;
  // }, [doNotShowCharts]);

  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRange,
    dateRangeInterval,
    compareDateRange,
    compareDateRangeInterval,
    breakdowns,
    filters,
    selectedSavedView,
    setAttributionModel,
    setAttributionWindow,
    setAccountingMode,
    setTimeGranularity,
    setDateRange,
    // setCompareDateRange,
    // setBreakdowns,
    // setFilters,
  } = useSalesPageV3NextContext();

  // TODO: This pattern of creating a page context just to log it is repeated in multiple components.
  // It might be worth creating a custom hook or context that progressively builds this context,
  // so calling `prettyLogEventPage` can be more straightforward.
  const pageContext = {
    dateRange,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    compareDateRange,
    breakdowns,
    filters,
  };

  const isAttributionWindowVisible = accountingMode === "accrual";
  const isHourlyTimeGranularity = timeGranularity === "hourly";

  return (
    <div className="py-4 flex justify-between space-x-4">
      <div className="flex items-end space-x-3 grow">
        <div className="text-2xl font-bold max-w-xxs truncate">
          {generateAnonBase64Hash({
            value: selectedSavedView.name,
            prefix: "quick-filter",
            doAnon: isDemoMode && !selectedSavedView.is_template,
          })}
        </div>
        <SavedViewsMenu placement="bottom-start" />
        <div className="border-l border-gray-1 h-8" />
        <AttributionModelSelect
          maxWidth="160px"
          attributionModel={attributionModel}
          onChange={(value) => {
            const newAttributionModel = value;

            prettyLogEventPage({
              domain: "Top Level Filters",
              action: "onChange",
              additionalActionInfo: "Set Attribution Model",
              data: {
                newAttributionModel,
                pageContext,
              },
            });

            setAttributionModel(newAttributionModel);
          }}
        />
        {isAttributionWindowVisible && (
          <GenericTooltip
            className={isHourlyTimeGranularity ? "cursor-not-allowed" : ""}
            content={
              isHourlyTimeGranularity ? (
                <>
                  Attribution window selection unavailable
                  <br />
                  for the time granularity selected
                </>
              ) : null
            }
            placement="bottom"
            noInfoCircle
          >
            <AttributionWindowSelect
              maxWidth="160px"
              attributionModel={attributionModel}
              attributionWindow={attributionWindow}
              isDisabled={isHourlyTimeGranularity}
              timeGranularity="daily"
              onChange={(value) => {
                const newAttributionWindow = value;

                prettyLogEventPage({
                  domain: "Top Level Filters",
                  action: "onChange",
                  additionalActionInfo: "Set Attribution Window",
                  data: {
                    newAttributionWindow,
                    pageContext,
                  },
                });

                setAttributionWindow(newAttributionWindow);
              }}
            />
          </GenericTooltip>
        )}
        <NbAccountingModeSelect
          maxWidth="200px"
          nbAccountingMode={accountingMode}
          isLoading={false}
          icon={null}
          onChange={(value) => {
            const newAccountingMode = value;

            prettyLogEventPage({
              domain: "Top Level Filters",
              action: "onChange",
              additionalActionInfo: "Set Accounting Mode",
              data: {
                newAccountingMode,
                pageContext,
              },
            });

            setAccountingMode(newAccountingMode);
          }}
        />
      </div>
      <div className="flex space-x-2 items-end">
        <SaveSavedViewsMenu />
        <ShareButton />
        <ExportMenu
          onChartExport={({ index }) =>
            // eslint-disable-next-line no-console
            console.log("Exporting chart data...", index)
          }
        />
        {/* <EllipsisMenu>
          <EllipsisMenu.Item
            as="button"
            onClick={onShowConfigurationDetailsModal}
          >
            Configuration details
          </EllipsisMenu.Item>
          {hasFeatureFlag("customMetricsV1") && (
            <EllipsisMenu.Item
              as="button"
              onClick={() => openCustomMetricsModal()}
            >
              Custom metrics
              <CustomMetricsNewBadge className="ml-2" />
            </EllipsisMenu.Item>
          )}
          <EllipsisMenu.Item
            as="button"
            onClick={() => {
              // onClick
            }}
          >
            <div className="flex items-center w-full">
              <div className="mr-2">Show charts experience</div>
              <Switch
                size="small"
                value={showCharts}
                onChange={() => {
                  prettyLogEventPage({
                    domain: "Top Level Filters",
                    action: "onChange",
                    additionalActionInfo: "Toggle Chart Experience",
                  });
                  setDoNotShowCharts((prev) => !prev);
                }}
                labelClassname="ml-auto"
                id="show-charts-experience-switch"
              />
            </div>
          </EllipsisMenu.Item>
          <EllipsisMenu.Item
            as="button"
            onClick={() => {
              // Do nothing
            }}
          >
            <NewSalesExperienceSwitch switchClassname="ml-auto" basic />
          </EllipsisMenu.Item>
        </EllipsisMenu> */}
      </div>
    </div>
  );
};
